import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Slider } from "../components/ui/slider";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import notificationService from "../services/notificationService";
import BodyDiagram from "../components/BodyDiagram";
import {
  Play,
  Pause,
  SkipForward,
  SkipBack,
  CheckCircle,
  ArrowLeft,
  Clock,
  Flame,
  Timer,
  ChevronRight,
  RotateCcw,
  Volume2,
  VolumeX,
  Coffee,
  Zap,
  Dumbbell,
  Music,
  Music2
} from "lucide-react";
import BeastModeMeter from "../components/BeastModeMeter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_3e64acab-2561-42e6-b860-06b1a563df4d/artifacts/5nvw3yv2_6ECA6BBA-F429-4238-A743-EE6BF2D1D33F.png";

// Workout Music Tracks - Royalty-free workout beats
const WORKOUT_TRACKS = [
  {
    id: 1,
    name: "Power Surge",
    artist: "Workout Beats",
    genre: "Electronic",
    // Using free Pixabay music URLs
    url: "https://cdn.pixabay.com/download/audio/2022/03/15/audio_8cb749d484.mp3",
    bpm: 140
  },
  {
    id: 2,
    name: "Beast Unleashed",
    artist: "Gym Energy",
    genre: "Hip-Hop",
    url: "https://cdn.pixabay.com/download/audio/2022/10/25/audio_946b0939c8.mp3",
    bpm: 128
  },
  {
    id: 3,
    name: "Iron Will",
    artist: "Strength Academy",
    genre: "Rock",
    url: "https://cdn.pixabay.com/download/audio/2022/05/27/audio_1808fbf07a.mp3",
    bpm: 135
  },
  {
    id: 4,
    name: "Cardio Rush",
    artist: "Fitness Flow",
    genre: "EDM",
    url: "https://cdn.pixabay.com/download/audio/2023/07/30/audio_e51bcab51b.mp3",
    bpm: 150
  }
];

const MUSCLE_COLORS = {
  chest: "#00FF94",
  triceps: "#00FF94",
  shoulders: "#00FF94",
  biceps: "#00FF94",
  back: "#00FF94",
  core: "#00FF94",
  abs: "#00FF94",
  obliques: "#00FF94",
  quads: "#00FF94",
  glutes: "#00FF94",
  hamstrings: "#00FF94",
  calves: "#00FF94",
  cardio: "#DC143C",
  full_body: "#DC143C"
};

// Audio beep function using Web Audio API
const createBeep = (frequency = 800, duration = 200, volume = 0.3) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = frequency;
    oscillator.type = "sine";
    gainNode.gain.value = volume;
    
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
      audioContext.close();
    }, duration);
  } catch (e) {
    console.log("Audio not supported");
  }
};

// YOUNG PERSONAL TRAINER Voice - Energetic, Clear, Encouraging
let voicesLoaded = false;
let availableVoices = [];

// Load voices when available
if ('speechSynthesis' in window) {
  const loadVoices = () => {
    availableVoices = window.speechSynthesis.getVoices();
    voicesLoaded = availableVoices.length > 0;
  };
  loadVoices();
  window.speechSynthesis.onvoiceschanged = loadVoices;
}

const speakText = (text, rate = 1.05, isHype = false) => {
  if (!('speechSynthesis' in window)) return;
  
  // Cancel any ongoing speech
  window.speechSynthesis.cancel();
  
  const utterance = new SpeechSynthesisUtterance(text);
  
  // YOUNG TRAINER STYLE - Natural pace, clear, enthusiastic
  utterance.rate = isHype ? 1.1 : rate; // Natural to slightly upbeat
  utterance.pitch = 1.05; // Slightly higher for youthful energy
  utterance.volume = 1;
  
  // Find a clear, natural sounding voice
  if (availableVoices.length > 0) {
    // Priority order for natural trainer voices
    const preferredNames = [
      'Google US English',
      'Samantha', // Mac - clear female
      'Microsoft Zira', // Windows - clear female
      'Microsoft David', // Windows - clear male
      'Alex', // Mac - clear male
      'Karen', // Australian - energetic
      'Daniel', // British - clear
    ];
    
    let selectedVoice = null;
    for (const name of preferredNames) {
      selectedVoice = availableVoices.find(v => v.name.includes(name));
      if (selectedVoice) break;
    }
    
    // Fallback to any English voice
    if (!selectedVoice) {
      selectedVoice = availableVoices.find(v => v.lang.startsWith('en'));
    }
    
    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }
  }
  
  // Speak naturally
  setTimeout(() => {
    window.speechSynthesis.speak(utterance);
  }, 50);
};

// YOUNG PERSONAL TRAINER Prompts - Encouraging, Supportive, Energetic
const HYPE_PROMPTS = {
  start: [
    "Alright, let's do this! You ready?",
    "Here we go! Give me everything you've got!",
    "Time to work! I believe in you!",
    "Let's get moving! You've got this!",
    "Okay, starting now! Stay with me!",
    "Let's crush this workout together!"
  ],
  nextSet: [
    "Nice! One more set, you're doing great!",
    "Awesome work! Ready for another?",
    "Keep it up! Next set, let's go!",
    "You're killing it! One more!",
    "Great form! Hit it again!"
  ],
  nextExercise: [
    "Perfect! Moving on to the next one!",
    "Nice work! New exercise coming up!",
    "Excellent! Let's switch it up!",
    "Great job! Next exercise, stay focused!"
  ],
  complete: [
    "Yes! Workout complete! You crushed it!",
    "Amazing work! You should be proud!",
    "Done! That was incredible!",
    "Finished! Great job showing up today!",
    "You did it! That's what I'm talking about!"
  ],
  midway: [
    "Halfway there! Don't stop now!",
    "You're doing amazing! Keep pushing!",
    "Stay with me! We're halfway done!",
    "Great pace! Keep that energy up!"
  ],
  rest: [
    "Take a breather! You earned it!",
    "Quick rest! Shake it out!",
    "Nice work! Catch your breath!",
    "Rest up! Next set coming soon!"
  ],
  countdown: [
    "3, 2, 1, let's go!",
    "Time! Great effort!"
  ],
  beastMode: [
    "Beast mode! Let's turn it up!",
    "Oh we're going hard now! I love it!",
    "Now that's what I'm talking about!"
  ],
  encouragement: [
    "You're stronger than you think!",
    "Every rep counts! Keep going!",
    "This is your time! Own it!",
    "Mind over matter! You've got this!"
  ]
};

const getRandomHypePrompt = (type) => {
  const prompts = HYPE_PROMPTS[type] || HYPE_PROMPTS.start;
  return prompts[Math.floor(Math.random() * prompts.length)];
};

export default function Workout() {
  const { day } = useParams();
  const navigate = useNavigate();
  const { user, fetchUser } = useAuth();
  const [workout, setWorkout] = useState(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [currentSet, setCurrentSet] = useState(1);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [showComplete, setShowComplete] = useState(false);
  const [coachMessage, setCoachMessage] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Brain Slide - Mindset screen before workout
  const [showBrainSlide, setShowBrainSlide] = useState(true);
  const [brainSlideStep, setBrainSlideStep] = useState(0);
  
  // Beast Mode - doubles reps and sets
  const [beastMode, setBeastMode] = useState(false);
  
  // Music Player states
  const [musicEnabled, setMusicEnabled] = useState(false);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [musicVolume, setMusicVolume] = useState(0.3);
  const [showMusicPlayer, setShowMusicPlayer] = useState(false);
  const audioRef = useRef(null);
  
  // Timer states
  const [timerMode, setTimerMode] = useState("exercise"); // exercise, rest, hiit
  const [restTime, setRestTime] = useState(30);
  const [isResting, setIsResting] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  
  // HIIT states
  const [hiitWorkTime, setHiitWorkTime] = useState(30);
  const [hiitRestTime, setHiitRestTime] = useState(10);
  const [hiitRounds, setHiitRounds] = useState(8);
  const [currentHiitRound, setCurrentHiitRound] = useState(1);
  const [isHiitWork, setIsHiitWork] = useState(true);
  const [showHiitModal, setShowHiitModal] = useState(false);
  
  const timerRef = useRef(null);
  
  // Brain Slide Content - Daily Affirmations & Fitness Questions
  const DAILY_AFFIRMATIONS = [
    "I am getting stronger with every workout.",
    "My body is capable of amazing things.",
    "I choose to push through discomfort to reach my goals.",
    "Every rep brings me closer to my best self.",
    "I am disciplined, focused, and unstoppable.",
    "Pain is temporary, but the pride of finishing lasts forever.",
    "I am worthy of the healthy body I am building.",
    "Today I will be better than yesterday.",
    "My consistency is my superpower.",
    "I am transforming, one workout at a time."
  ];
  
  const FITNESS_QUESTIONS = [
    { question: "What's the main benefit of compound exercises?", answer: "They work multiple muscle groups at once, maximizing efficiency and calorie burn." },
    { question: "Why is rest between sets important?", answer: "It allows ATP regeneration so your muscles can perform at full capacity for the next set." },
    { question: "How does muscle growth actually happen?", answer: "Exercise creates micro-tears in muscle fibers. During rest, your body repairs them stronger than before." },
    { question: "What's the mind-muscle connection?", answer: "Consciously focusing on the muscle being worked increases muscle fiber recruitment by up to 20%." },
    { question: "Why warm up before lifting?", answer: "It increases blood flow, raises muscle temperature, and prepares joints for movement—reducing injury risk." },
    { question: "What makes HIIT so effective?", answer: "It creates an 'afterburn effect' where your body continues burning calories for hours post-workout." },
    { question: "Why is form more important than weight?", answer: "Proper form targets the intended muscles and prevents injury. Bad form limits gains and risks damage." },
    { question: "How long does it take to see muscle gains?", answer: "Neurological adaptations start immediately; visible muscle growth typically shows in 4-8 weeks." }
  ];
  
  const BRAIN_SLIDES = [
    {
      type: "affirmation",
      title: "TODAY'S AFFIRMATION",
      subtitle: "Speak It. Believe It. Become It.",
      content: DAILY_AFFIRMATIONS[Math.floor(Math.random() * DAILY_AFFIRMATIONS.length)],
      icon: "✨",
      tip: "Say this out loud. Your mind believes what you tell it."
    },
    {
      type: "question",
      title: "FITNESS KNOWLEDGE",
      subtitle: "Learn While You Burn",
      ...FITNESS_QUESTIONS[Math.floor(Math.random() * FITNESS_QUESTIONS.length)],
      icon: "🧠",
      tip: "Understanding your body helps you train smarter."
    },
    {
      type: "mindset",
      title: "MENTAL PREP",
      subtitle: "Visualize Your Success",
      content: "Close your eyes. Picture yourself completing every rep with perfect form. Feel the burn. See yourself finishing strong. Now open your eyes—let's make it real.",
      icon: "🔥",
      tip: "Champions visualize victory before they achieve it."
    }
  ];
  
  // Beast Mode multiplier
  const getBeastMultiplier = () => beastMode ? 2 : 1;
  const getExerciseReps = (exercise) => Math.round((exercise?.reps || 0) * getBeastMultiplier());
  const getExerciseSets = (exercise) => Math.round((exercise?.sets || 3) * getBeastMultiplier());
  const getExerciseDuration = (exercise) => Math.round((exercise?.duration_seconds || 60) * (beastMode ? 1.5 : 1));

  useEffect(() => {
    fetchWorkout();
    // Load speech synthesis voices
    if ('speechSynthesis' in window) {
      window.speechSynthesis.getVoices();
    }
  }, [day]);

  // Main timer effect
  useEffect(() => {
    if (isPlaying && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          // Audio cues at specific times
          if (soundEnabled) {
            if (prev === 4) createBeep(600, 150, 0.2);
            if (prev === 3) createBeep(600, 150, 0.2);
            if (prev === 2) createBeep(600, 150, 0.2);
            if (prev === 1) createBeep(800, 300, 0.4);
          }
          
          if (prev <= 1) {
            handleTimerComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    
    return () => clearInterval(timerRef.current);
  }, [isPlaying, timerMode, isResting, isHiitWork, soundEnabled]);

  // Music Player Effect
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = musicVolume;
      if (musicEnabled) {
        audioRef.current.play().catch(e => console.log("Audio play failed:", e));
      } else {
        audioRef.current.pause();
      }
    }
  }, [musicEnabled, musicVolume]);
  
  // Handle track change
  useEffect(() => {
    if (audioRef.current && musicEnabled) {
      audioRef.current.load();
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    }
  }, [currentTrackIndex]);

  // Brain Slide Voice Effect - speak content when slide changes
  useEffect(() => {
    if (showBrainSlide && voiceEnabled) {
      const slide = BRAIN_SLIDES[brainSlideStep];
      let textToSpeak = "";
      
      if (slide.type === "affirmation") {
        textToSpeak = `${slide.title}. ${slide.content}. Remember, ${slide.tip}`;
      } else if (slide.type === "question") {
        textToSpeak = `Fitness Knowledge. ${slide.question}`;
        // Speak the answer after a delay
        setTimeout(() => {
          if (voiceEnabled && showBrainSlide) {
            speakText(`The answer is: ${slide.answer}`, 1.0, false);
          }
        }, 3000);
      } else if (slide.type === "mindset") {
        textToSpeak = `${slide.title}. ${slide.content}`;
      }
      
      if (textToSpeak) {
        speakText(textToSpeak, 0.95, false);
      }
    }
  }, [brainSlideStep, showBrainSlide, voiceEnabled]);

  // Music controls
  const toggleMusic = () => {
    setMusicEnabled(!musicEnabled);
    if (!musicEnabled) {
      toast("🎵 Music ON", { duration: 1500 });
    }
  };
  
  const nextTrack = () => {
    setCurrentTrackIndex((prev) => (prev + 1) % WORKOUT_TRACKS.length);
  };
  
  const prevTrack = () => {
    setCurrentTrackIndex((prev) => (prev - 1 + WORKOUT_TRACKS.length) % WORKOUT_TRACKS.length);
  };
  
  const currentTrack = WORKOUT_TRACKS[currentTrackIndex];

  const handleTimerComplete = () => {
    setIsPlaying(false);
    
    if (timerMode === "hiit") {
      if (isHiitWork) {
        // Work period ended, start rest
        if (soundEnabled) createBeep(400, 500, 0.3);
        if (voiceEnabled) speakText(getRandomHypePrompt('rest'), 1.05, true);
        setIsHiitWork(false);
        setTimeLeft(hiitRestTime);
        setIsPlaying(true);
      } else {
        // Rest period ended
        if (currentHiitRound < hiitRounds) {
          if (soundEnabled) createBeep(800, 300, 0.4);
          if (voiceEnabled) speakText(`Round ${currentHiitRound + 1}. Let's go.`, 1.05, true);
          setCurrentHiitRound(prev => prev + 1);
          setIsHiitWork(true);
          setTimeLeft(hiitWorkTime);
          setIsPlaying(true);
        } else {
          // HIIT complete
          if (soundEnabled) createBeep(1000, 500, 0.5);
          if (voiceEnabled) speakText(getRandomHypePrompt('complete'), 1.05, true);
          toast.success("HIIT workout complete!");
          setShowHiitModal(false);
          setTimerMode("exercise");
        }
      }
    } else if (isResting) {
      // Rest period ended
      if (soundEnabled) createBeep(800, 300, 0.4);
      if (voiceEnabled) speakText(getRandomHypePrompt('nextSet'), 1.05, true);
      setIsResting(false);
      toast.success("Rest complete! Let's go!");
    } else {
      // Exercise timer ended
      if (soundEnabled) createBeep(600, 200, 0.3);
      if (voiceEnabled) speakText("Time. Great work.", 1.05, true);
      toast.success("Time's up! Great work!");
    }
  };

  const fetchWorkout = async () => {
    try {
      const response = await axios.get(`${API}/workouts/${day}`);
      setWorkout(response.data);
      
      if (response.data.exercises?.length > 0) {
        const firstExercise = response.data.exercises[0];
        setTimeLeft(firstExercise.duration_seconds || 60);
      }
      
      // Get coaching message
      const coachRes = await axios.post(`${API}/coaching/message`, { context: "workout_start" });
      setCoachMessage(coachRes.data.message);
      
      // Speak welcome message
      if (voiceEnabled) {
        setTimeout(() => speakText(coachRes.data.message, 1.05), 1000);
      }
    } catch (error) {
      // Check if subscription error
      if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || "Subscription required");
        navigate("/pricing");
      } else {
        toast.error("Failed to load workout");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  const currentExercise = workout?.exercises?.[currentExerciseIndex];
  const totalExercises = workout?.exercises?.length || 0;
  const exerciseProgress = totalExercises > 0 ? ((currentExerciseIndex + 1) / totalExercises) * 100 : 0;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const resetTimer = () => {
    setIsPlaying(false);
    setTimeLeft(getExerciseDuration(currentExercise));
  };

  const startRestTimer = () => {
    setIsResting(true);
    setTimeLeft(restTime);
    setIsPlaying(true);
    if (voiceEnabled) speakText(getRandomHypePrompt('rest'), 1.05, true);
  };

  const startHiitTimer = () => {
    setTimerMode("hiit");
    setCurrentHiitRound(1);
    setIsHiitWork(true);
    setTimeLeft(hiitWorkTime);
    setShowHiitModal(true);
    if (voiceEnabled) speakText("HIIT mode. Get ready to work.", 1.05, true);
  };

  const handleNextSet = () => {
    const totalSets = getExerciseSets(currentExercise);
    if (currentSet < totalSets) {
      setCurrentSet(prev => prev + 1);
      setTimeLeft(getExerciseDuration(currentExercise));
      setIsPlaying(false);
      startRestTimer();
    } else {
      handleNextExercise();
    }
  };

  const handleNextExercise = async () => {
    setCompletedExercises(prev => [...prev, currentExerciseIndex]);
    
    if (currentExerciseIndex < totalExercises - 1) {
      setCurrentExerciseIndex(prev => prev + 1);
      setCurrentSet(1);
      const nextExercise = workout.exercises[currentExerciseIndex + 1];
      setTimeLeft(nextExercise?.duration_seconds || 60);
      setIsPlaying(false);
      setIsResting(false);
      
      if (voiceEnabled) {
        speakText(`${getRandomHypePrompt('nextExercise')} ${nextExercise?.name}.`, 1.05, true);
      }
      
      // Mid-workout message
      if (currentExerciseIndex === Math.floor(totalExercises / 2)) {
        try {
          const coachRes = await axios.post(`${API}/coaching/message`, { context: "workout_mid" });
          toast(coachRes.data.message, { icon: "💪" });
          if (voiceEnabled) speakText(getRandomHypePrompt('midway'), 1.05, true);
        } catch (e) { /* Non-critical coaching message */ }
      }
    } else {
      setShowComplete(true);
      if (voiceEnabled) speakText(getRandomHypePrompt('complete'), 1.05, true);
    }
  };

  const handleCompleteWorkout = async () => {
    try {
      await axios.post(`${API}/workouts/${day}/complete`);
      await fetchUser();
      toast.success("Workout complete! Great work!");
      
      // Send push notification for workout completion
      try {
        const statsRes = await axios.get(`${API}/profile/stats`);
        const streak = statsRes.data?.streak || 1;
        await notificationService.sendWorkoutComplete(day, streak);
      } catch (e) {
        console.log("Could not send completion notification");
      }
      
      // End workout message
      try {
        const coachRes = await axios.post(`${API}/coaching/message`, { context: "workout_end" });
        toast(coachRes.data.message, { icon: "🔥", duration: 5000 });
        if (voiceEnabled) speakText(getRandomHypePrompt('complete'), 1.05, true);
      } catch (e) {}
      
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to log workout");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary animate-pulse text-2xl font-bold">LOADING WORKOUT...</div>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Workout Not Found</h2>
          <Button onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  // Rest day
  if (workout.exercises?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16">
              <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-5 w-5" /> Back
              </Link>
            </div>
          </div>
        </header>
        
        <main className="pt-24 pb-12 px-4 max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="w-24 h-24 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-accent" />
            </div>
            <h1 className="text-4xl font-black uppercase tracking-tighter mb-4">{workout.title}</h1>
            <p className="text-xl text-muted-foreground mb-8">{workout.description}</p>
            <Button onClick={() => navigate("/dashboard")} className="bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm">
              Back to Dashboard
            </Button>
          </motion.div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Brain Slide - Pre-Workout Mental Prep */}
      {showBrainSlide && (
        <div className="fixed inset-0 z-50 bg-background">
          <div className="min-h-screen flex flex-col">
            {/* Progress Dots & Voice Toggle */}
            <div className="flex items-center justify-center gap-4 pt-8">
              <div className="flex gap-2">
                {BRAIN_SLIDES.map((_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      i === brainSlideStep 
                        ? 'bg-primary scale-110' 
                        : i < brainSlideStep 
                        ? 'bg-accent' 
                        : 'bg-secondary'
                    }`}
                  />
                ))}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  setVoiceEnabled(!voiceEnabled);
                  if (voiceEnabled) {
                    window.speechSynthesis.cancel();
                  }
                }}
                className={voiceEnabled ? 'text-accent' : 'text-muted-foreground'}
              >
                {voiceEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
              </Button>
            </div>
            
            {/* Brain Slide Content */}
            <div className="flex-1 flex items-center justify-center px-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={brainSlideStep}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  transition={{ duration: 0.4 }}
                  className="w-full max-w-lg text-center"
                >
                  {/* Icon */}
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="text-6xl mb-6"
                  >
                    {BRAIN_SLIDES[brainSlideStep].icon}
                  </motion.div>
                  
                  {/* Title */}
                  <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-tighter text-primary mb-2">
                    {BRAIN_SLIDES[brainSlideStep].title}
                  </h2>
                  <p className="text-muted-foreground uppercase tracking-wider text-sm mb-8">
                    {BRAIN_SLIDES[brainSlideStep].subtitle}
                  </p>
                  
                  {/* Content Card */}
                  <Card className="bg-card/50 border-primary/30 backdrop-blur-sm">
                    <CardContent className="p-6 sm:p-8">
                      {BRAIN_SLIDES[brainSlideStep].type === 'question' ? (
                        <>
                          <p className="text-xl sm:text-2xl font-bold text-foreground mb-4">
                            {BRAIN_SLIDES[brainSlideStep].question}
                          </p>
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ delay: 1.5, duration: 0.5 }}
                            className="overflow-hidden"
                          >
                            <div className="pt-4 border-t border-border">
                              <p className="text-accent font-medium">
                                {BRAIN_SLIDES[brainSlideStep].answer}
                              </p>
                            </div>
                          </motion.div>
                        </>
                      ) : (
                        <p className="text-xl sm:text-2xl font-medium text-foreground leading-relaxed">
                          "{BRAIN_SLIDES[brainSlideStep].content}"
                        </p>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Tip */}
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-6 text-sm text-muted-foreground italic"
                  >
                    💡 {BRAIN_SLIDES[brainSlideStep].tip}
                  </motion.p>
                </motion.div>
              </AnimatePresence>
            </div>
            
            {/* Navigation Buttons */}
            <div className="p-6 pb-10">
              <div className="flex gap-4 max-w-lg mx-auto">
                {brainSlideStep > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => setBrainSlideStep(prev => prev - 1)}
                    className="flex-1 uppercase font-bold tracking-widest rounded-sm"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" /> Back
                  </Button>
                )}
                <Button
                  data-testid="brain-slide-next-btn"
                  onClick={() => {
                    if (brainSlideStep < BRAIN_SLIDES.length - 1) {
                      setBrainSlideStep(prev => prev + 1);
                    } else {
                      window.speechSynthesis.cancel(); // Stop any ongoing speech
                      setShowBrainSlide(false);
                      if (voiceEnabled) {
                        speakText("Let's do this! Time to work!", 1.05, true);
                      }
                    }
                  }}
                  className={`flex-1 uppercase font-bold tracking-widest rounded-sm ${
                    brainSlideStep === BRAIN_SLIDES.length - 1 
                      ? 'bg-accent text-black hover:bg-accent/90' 
                      : 'bg-primary hover:bg-primary/90'
                  }`}
                >
                  {brainSlideStep === BRAIN_SLIDES.length - 1 ? (
                    <>
                      <Flame className="mr-2 h-4 w-4" /> Let's Crush It!
                    </>
                  ) : (
                    <>
                      Next <ChevronRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
              
              {/* Skip Option */}
              <button
                onClick={() => {
                  window.speechSynthesis.cancel(); // Stop speech when skipping
                  setShowBrainSlide(false);
                }}
                className="mt-4 text-xs text-muted-foreground hover:text-foreground uppercase tracking-wider mx-auto block"
              >
                Skip Mental Prep →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" /> Exit
            </Link>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="Coach Coop" className="h-8 w-8 rounded-sm" />
              <span className="font-bold tracking-tight">DAY {day}</span>
            </div>
            <div className="flex items-center gap-2">
              {/* Music Toggle */}
              <Button
                data-testid="music-toggle-btn"
                variant="ghost"
                size="icon"
                onClick={() => setShowMusicPlayer(!showMusicPlayer)}
                className={musicEnabled ? 'text-accent' : 'text-muted-foreground'}
              >
                <Music className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={soundEnabled ? 'text-primary' : 'text-muted-foreground'}
              >
                {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </Button>
              <div className="flex items-center gap-1 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span className="text-sm">{workout.duration_minutes} min</span>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      {/* Hidden Audio Element */}
      <audio 
        ref={audioRef} 
        loop 
        preload="auto"
        onEnded={nextTrack}
      >
        <source src={currentTrack?.url} type="audio/mpeg" />
      </audio>
      
      {/* Floating Music Player */}
      <AnimatePresence>
        {showMusicPlayer && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="fixed bottom-24 right-4 z-50 w-72"
          >
            <Card className="bg-card/95 backdrop-blur-lg border-accent/30">
              <CardContent className="p-4">
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <motion.div
                      animate={musicEnabled ? { rotate: 360 } : {}}
                      transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    >
                      <Music2 className="h-5 w-5 text-accent" />
                    </motion.div>
                    <span className="text-sm font-bold uppercase tracking-wider text-accent">Workout Music</span>
                  </div>
                  <button
                    onClick={() => setShowMusicPlayer(false)}
                    className="text-muted-foreground hover:text-foreground text-xs"
                  >
                    ✕
                  </button>
                </div>
                
                {/* Track Info */}
                <div className="mb-3 p-3 bg-secondary/50 rounded-sm">
                  <div className="text-sm font-bold truncate">{currentTrack?.name}</div>
                  <div className="text-xs text-muted-foreground">{currentTrack?.artist} • {currentTrack?.genre}</div>
                  <div className="text-xs text-accent/70 mt-1">{currentTrack?.bpm} BPM</div>
                </div>
                
                {/* Controls */}
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={prevTrack}
                    className="h-8 w-8"
                  >
                    <SkipBack className="h-4 w-4" />
                  </Button>
                  <Button
                    data-testid="music-play-btn"
                    onClick={toggleMusic}
                    className={`h-12 w-12 rounded-full ${
                      musicEnabled 
                        ? 'bg-accent text-black hover:bg-accent/90' 
                        : 'bg-primary text-white hover:bg-primary/90'
                    }`}
                  >
                    {musicEnabled ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={nextTrack}
                    className="h-8 w-8"
                  >
                    <SkipForward className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Volume */}
                <div className="flex items-center gap-2">
                  <Volume2 className="h-4 w-4 text-muted-foreground" />
                  <Slider
                    value={[musicVolume * 100]}
                    onValueChange={([val]) => setMusicVolume(val / 100)}
                    max={100}
                    step={5}
                    className="flex-1"
                  />
                  <span className="text-xs text-muted-foreground w-8">{Math.round(musicVolume * 100)}%</span>
                </div>
                
                {/* Track List */}
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Tracks</div>
                  <div className="space-y-1 max-h-28 overflow-y-auto">
                    {WORKOUT_TRACKS.map((track, i) => (
                      <button
                        key={track.id}
                        onClick={() => setCurrentTrackIndex(i)}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                          i === currentTrackIndex 
                            ? 'bg-accent/20 text-accent' 
                            : 'hover:bg-secondary text-muted-foreground'
                        }`}
                      >
                        {track.name}
                      </button>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress Bar */}
      <div className="fixed top-16 left-0 right-0 z-30">
        <Progress value={exerciseProgress} className="h-1 rounded-none" />
      </div>

      <main className="pt-20 pb-32">
        {/* Workout Title */}
        <div className="px-4 py-6 text-center border-b border-border">
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tighter text-primary">{workout.title}</h1>
          <p className="text-muted-foreground">{workout.description}</p>
          
          {/* Beast Mode Toggle */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4"
          >
            <Button
              data-testid="beast-mode-toggle"
              onClick={() => {
                const newBeastMode = !beastMode;
                setBeastMode(newBeastMode);
                if (newBeastMode && voiceEnabled) {
                  speakText(getRandomHypePrompt('beastMode'), 1.05, true);
                }
                toast(newBeastMode ? "🦁 BEAST MODE ON - 2x Reps & Sets!" : "Beast Mode OFF", {
                  icon: newBeastMode ? "🔥" : "😌",
                  duration: 2000
                });
              }}
              className={`px-6 py-3 font-black uppercase tracking-widest rounded-sm transition-all duration-300 ${
                beastMode 
                  ? 'bg-gradient-to-r from-orange-500 via-red-500 to-orange-500 text-white beast-mode-pulse' 
                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}
            >
              <Flame className={`h-5 w-5 mr-2 inline ${beastMode ? 'animate-bounce' : ''}`} />
              {beastMode ? '🦁 BEAST MODE ON' : 'Activate Beast Mode'}
            </Button>
            {beastMode && (
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="text-xs text-orange-400 mt-2 uppercase tracking-wider font-bold"
              >
                2x Reps • 2x Sets • 1.5x Duration
              </motion.p>
            )}
            
            {/* Beast Mode Meter */}
            {beastMode && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 max-w-md mx-auto"
              >
                <BeastModeMeter
                  exerciseName={currentExercise?.name}
                  repsCompleted={currentSet}
                  autoIncrement={isPlaying && beastMode}
                  onBeastModeActivated={() => {
                    if (voiceEnabled) {
                      speakText("BEAST MODE MAXIMUM! UNLEASH THE BEAST!", 1.2, true);
                    }
                    toast.success("💀🔥 BEAST MODE MAXIMUM!", { duration: 3000 });
                  }}
                />
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* Coach Message */}
        {coachMessage && !isResting && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="px-4 py-4 bg-primary/10 text-center"
          >
            <p className="text-sm italic">"{coachMessage}"</p>
          </motion.div>
        )}

        {/* Rest Timer Overlay */}
        {isResting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="px-4 py-8 bg-blue-500/20 text-center border-y border-blue-500/30"
          >
            <Coffee className="h-8 w-8 text-blue-400 mx-auto mb-2" />
            <div className="text-sm text-blue-400 uppercase tracking-wider mb-2">Rest Period</div>
            <div className="text-6xl font-mono font-black text-blue-400">{formatTime(timeLeft)}</div>
            <div className="mt-4 flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setIsResting(false); setTimeLeft(getExerciseDuration(currentExercise)); }}
                className="border-blue-400 text-blue-400"
              >
                Skip Rest
              </Button>
            </div>
          </motion.div>
        )}

        {/* Current Exercise */}
        {!isResting && (
          <div className="px-4 py-6 max-w-2xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentExerciseIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                {/* Exercise Counter */}
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-muted-foreground uppercase tracking-wider">
                    Exercise {currentExerciseIndex + 1} of {totalExercises}
                  </span>
                  <span className={`text-sm uppercase tracking-wider ${beastMode ? 'text-orange-400 font-bold' : 'text-muted-foreground'}`}>
                    Set {currentSet} of {getExerciseSets(currentExercise)}
                  </span>
                </div>

                {/* Exercise Video/GIF */}
                <Card className="bg-card border-border overflow-hidden mb-6">
                  <div className="aspect-video bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 relative flex items-center justify-center">
                    {currentExercise?.gif_url ? (
                      <img 
                        src={currentExercise.gif_url} 
                        alt={currentExercise.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    {/* Fallback Exercise Display */}
                    <div 
                      className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"
                      style={{ display: currentExercise?.gif_url ? 'none' : 'flex' }}
                    >
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="w-24 h-24 rounded-full bg-primary/20 flex items-center justify-center mb-4"
                      >
                        <Dumbbell className="h-12 w-12 text-primary" />
                      </motion.div>
                      <h3 className="text-2xl font-black uppercase tracking-tighter text-primary mb-2">
                        {currentExercise?.name}
                      </h3>
                      <p className="text-muted-foreground text-center max-w-xs px-4">
                        {currentExercise?.description}
                      </p>
                      <div className="mt-4 flex gap-2">
                        {currentExercise?.muscle_groups?.slice(0, 3).map((muscle, i) => (
                          <span key={i} className="px-3 py-1 text-xs font-bold uppercase bg-[#00FF94]/20 text-[#00FF94] rounded-full">
                            {muscle.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </Card>
                
                {/* FULL BODY MUSCLE DIAGRAM - Gym Motivated Design */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6"
                >
                  <Card className="bg-gradient-to-b from-black via-gray-900 to-black border-2 border-[#00FF94]/50 overflow-hidden">
                    {/* Header */}
                    <div className="p-4 bg-gradient-to-r from-[#00FF94]/20 to-transparent border-b border-[#00FF94]/30">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <motion.div
                            animate={{ rotate: [0, 10, -10, 0] }}
                            transition={{ duration: 1, repeat: Infinity }}
                          >
                            <Dumbbell className="h-6 w-6 text-[#00FF94]" />
                          </motion.div>
                          <div>
                            <h3 className="text-lg font-black uppercase tracking-wider text-[#00FF94]">
                              Target Muscles
                            </h3>
                            <p className="text-xs text-muted-foreground uppercase tracking-wider">
                              {currentExercise?.name}
                            </p>
                          </div>
                        </div>
                        <motion.div
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1.5, repeat: Infinity }}
                          className="flex items-center gap-2"
                        >
                          <Zap className="h-5 w-5 text-[#00FF94]" />
                          <span className="text-xs text-[#00FF94] uppercase tracking-wider font-bold">Active</span>
                        </motion.div>
                      </div>
                    </div>
                    
                    {/* Body Diagram */}
                    <div className="p-6 flex justify-center">
                      <BodyDiagram activeMusles={currentExercise?.muscle_groups || []} />
                    </div>
                    
                    {/* Muscle Tags */}
                    <div className="p-4 border-t border-[#00FF94]/30 bg-black/50">
                      <div className="flex flex-wrap justify-center gap-2">
                        {currentExercise?.muscle_groups?.map((muscle, i) => (
                          <motion.span
                            key={i}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ delay: i * 0.1 }}
                            className="px-4 py-2 text-sm font-black uppercase tracking-wider rounded-sm bg-[#00FF94]/20 text-[#00FF94] border-2 border-[#00FF94] muscle-glow"
                          >
                            {muscle.replace('_', ' ')}
                          </motion.span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Motivational Footer */}
                    <motion.div 
                      className="px-4 py-3 bg-gradient-to-r from-primary/20 via-transparent to-primary/20 text-center"
                      animate={{ opacity: [0.7, 1, 0.7] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <span className="text-xs uppercase tracking-widest text-primary font-bold">
                        💪 Feel The Burn • Build The Muscle • Earn The Results 🔥
                      </span>
                    </motion.div>
                  </Card>
                </motion.div>

                  <Card className="bg-card border-border">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-black uppercase tracking-tighter mb-2">{currentExercise?.name}</h2>
                    <p className="text-muted-foreground mb-4">{currentExercise?.description}</p>
                    
                    {/* Beast Mode Indicator */}
                    {beastMode && (
                      <motion.div 
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mb-4 p-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/50 rounded-sm text-center"
                      >
                        <span className="text-orange-400 text-xs uppercase tracking-widest font-bold">🦁 Beast Mode Active</span>
                      </motion.div>
                    )}
                    
                    {/* Timer Display */}
                    <div className="flex items-center justify-center gap-8 mb-6">
                      {currentExercise?.duration_seconds ? (
                        <div className="text-center">
                          <div className={`text-6xl font-mono font-black ${isPlaying ? 'text-primary' : 'text-foreground'} ${beastMode ? 'text-orange-400' : ''}`}>
                            {formatTime(timeLeft)}
                          </div>
                          <div className="text-sm text-muted-foreground uppercase tracking-wider mt-1">Time</div>
                        </div>
                      ) : (
                        <div className="text-center">
                          <div className={`text-6xl font-mono font-black ${beastMode ? 'text-orange-400' : 'text-accent'}`}>
                            {getExerciseReps(currentExercise)}
                          </div>
                          <div className="text-sm text-muted-foreground uppercase tracking-wider mt-1">Reps</div>
                        </div>
                      )}
                      
                      <div className="text-center">
                        <div className={`text-4xl font-mono font-black ${beastMode ? 'text-orange-400' : 'text-muted-foreground'}`}>
                          {getExerciseSets(currentExercise)}
                        </div>
                        <div className="text-sm text-muted-foreground uppercase tracking-wider mt-1">Sets</div>
                      </div>
                    </div>

                    {/* Timer Controls */}
                    {currentExercise?.duration_seconds && (
                      <div className="flex items-center justify-center gap-3 mb-4">
                        <Button
                          data-testid="timer-reset-btn"
                          variant="outline"
                          size="icon"
                          onClick={resetTimer}
                          className="rounded-full"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </Button>
                        <Button
                          data-testid="timer-play-pause-btn"
                          size="lg"
                          onClick={() => {
                            if (!isPlaying && voiceEnabled && timeLeft === (getExerciseDuration(currentExercise))) {
                              speakText(getRandomHypePrompt('start'), 1.05, true);
                            }
                            setIsPlaying(!isPlaying);
                          }}
                          className={`${isPlaying ? 'bg-orange-500 hover:bg-orange-600' : 'bg-primary hover:bg-primary/90'} rounded-full w-16 h-16`}
                        >
                          {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-1" />}
                        </Button>
                        <Button
                          data-testid="timer-skip-btn"
                          variant="outline"
                          size="icon"
                          onClick={handleNextSet}
                          className="rounded-full"
                        >
                          <SkipForward className="h-4 w-4" />
                        </Button>
                      </div>
                    )}

                    {/* Rest Timer Setting */}
                    <div className="bg-secondary/50 rounded-sm p-4 mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-muted-foreground uppercase tracking-wider">Rest Between Sets</span>
                        <span className="font-mono font-bold">{restTime}s</span>
                      </div>
                      <Slider
                        value={[restTime]}
                        onValueChange={([val]) => setRestTime(val)}
                        min={10}
                        max={120}
                        step={5}
                        className="w-full"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3">
                  <Button
                    data-testid="workout-next-btn"
                    size="lg"
                    onClick={handleNextSet}
                    className="w-full bg-primary hover:bg-primary/90 uppercase font-bold tracking-widest rounded-sm"
                  >
                    {currentSet < (currentExercise?.sets || 3) ? 'Complete Set' : 'Next Exercise'}
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                  
                  <div className="flex gap-3">
                    <Button
                      data-testid="start-rest-btn"
                      variant="outline"
                      onClick={startRestTimer}
                      className="flex-1 uppercase font-bold tracking-widest rounded-sm"
                    >
                      <Coffee className="mr-2 h-4 w-4" /> Rest {restTime}s
                    </Button>
                    <Button
                      data-testid="start-hiit-btn"
                      variant="outline"
                      onClick={startHiitTimer}
                      className="flex-1 uppercase font-bold tracking-widest rounded-sm border-orange-500/50 text-orange-500 hover:bg-orange-500 hover:text-white"
                    >
                      <Zap className="mr-2 h-4 w-4" /> HIIT Timer
                    </Button>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        )}

        {/* Exercise List */}
        <div className="px-4 max-w-2xl mx-auto mt-8">
          <h3 className="text-sm text-muted-foreground uppercase tracking-wider mb-4">Workout Overview</h3>
          <div className="space-y-2">
            {workout.exercises?.map((exercise, i) => (
              <div
                key={i}
                className={`flex items-center gap-4 p-3 rounded-sm border ${
                  completedExercises.includes(i)
                    ? 'border-accent/30 bg-accent/10'
                    : i === currentExerciseIndex
                    ? 'border-primary bg-primary/10'
                    : 'border-border bg-card'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  completedExercises.includes(i)
                    ? 'bg-accent text-black'
                    : i === currentExerciseIndex
                    ? 'bg-primary text-white'
                    : 'bg-secondary text-muted-foreground'
                }`}>
                  {completedExercises.includes(i) ? <CheckCircle className="h-4 w-4" /> : i + 1}
                </div>
                <div className="flex-1">
                  <div className={`font-bold uppercase tracking-tight text-sm ${
                    completedExercises.includes(i) ? 'text-accent' : ''
                  }`}>
                    {exercise.name}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {exercise.duration_seconds ? `${exercise.duration_seconds}s` : `${exercise.reps} reps`} x {exercise.sets} sets
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* HIIT Timer Modal */}
      {showHiitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md"
          >
            <Card className="bg-card border-orange-500">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <Zap className="h-12 w-12 text-orange-500 mx-auto mb-2" />
                  <h3 className="text-2xl font-black uppercase tracking-tighter">HIIT TIMER</h3>
                  <div className="text-sm text-muted-foreground">
                    Round {currentHiitRound} of {hiitRounds}
                  </div>
                </div>
                
                <div className={`text-center p-6 rounded-sm mb-6 ${isHiitWork ? 'bg-orange-500/20' : 'bg-blue-500/20'}`}>
                  <div className={`text-sm uppercase tracking-wider mb-2 ${isHiitWork ? 'text-orange-500' : 'text-blue-400'}`}>
                    {isHiitWork ? 'WORK' : 'REST'}
                  </div>
                  <div className={`text-7xl font-mono font-black ${isHiitWork ? 'text-orange-500' : 'text-blue-400'}`}>
                    {formatTime(timeLeft)}
                  </div>
                </div>
                
                {!isPlaying && currentHiitRound === 1 && isHiitWork && timeLeft === hiitWorkTime && (
                  <div className="space-y-4 mb-6">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Work Time</span>
                        <span className="font-mono">{hiitWorkTime}s</span>
                      </div>
                      <Slider
                        value={[hiitWorkTime]}
                        onValueChange={([val]) => { setHiitWorkTime(val); setTimeLeft(val); }}
                        min={10}
                        max={60}
                        step={5}
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Rest Time</span>
                        <span className="font-mono">{hiitRestTime}s</span>
                      </div>
                      <Slider
                        value={[hiitRestTime]}
                        onValueChange={([val]) => setHiitRestTime(val)}
                        min={5}
                        max={30}
                        step={5}
                      />
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span>Rounds</span>
                        <span className="font-mono">{hiitRounds}</span>
                      </div>
                      <Slider
                        value={[hiitRounds]}
                        onValueChange={([val]) => setHiitRounds(val)}
                        min={4}
                        max={20}
                        step={1}
                      />
                    </div>
                  </div>
                )}
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowHiitModal(false);
                      setTimerMode("exercise");
                      setIsPlaying(false);
                    }}
                    className="flex-1 uppercase font-bold tracking-widest rounded-sm"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      if (!isPlaying && voiceEnabled) {
                        speakText("3. 2. 1. Go!", 1.05, true);
                      }
                      setIsPlaying(!isPlaying);
                    }}
                    className={`flex-1 uppercase font-bold tracking-widest rounded-sm ${
                      isPlaying ? 'bg-orange-500 hover:bg-orange-600' : 'bg-primary hover:bg-primary/90'
                    }`}
                  >
                    {isPlaying ? <><Pause className="mr-2 h-4 w-4" /> Pause</> : <><Play className="mr-2 h-4 w-4" /> Start</>}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}

      {/* Complete Workout Modal */}
      {showComplete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg"
          >
            <Card className="bg-card border-accent">
              <CardContent className="p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.2 }}
                  className="w-24 h-24 bg-accent/20 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Flame className="h-12 w-12 text-accent" />
                </motion.div>
                
                <h2 className="text-3xl font-black uppercase tracking-tighter mb-2">WORKOUT COMPLETE!</h2>
                <p className="text-muted-foreground mb-6">Day {day} in the books. That's discipline.</p>
                
                <div className="flex items-center justify-center gap-8 mb-8">
                  <div className="text-center">
                    <div className="text-3xl font-black text-primary font-mono">{totalExercises}</div>
                    <div className="text-sm text-muted-foreground uppercase">Exercises</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-accent font-mono">{workout.duration_minutes}</div>
                    <div className="text-sm text-muted-foreground uppercase">Minutes</div>
                  </div>
                </div>
                
                <Button
                  data-testid="complete-workout-btn"
                  size="lg"
                  onClick={handleCompleteWorkout}
                  className="w-full bg-accent text-black hover:bg-accent/90 uppercase font-bold tracking-widest rounded-sm"
                >
                  <CheckCircle className="mr-2 h-5 w-5" /> Log & Continue
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </div>
  );
}
